import { Section } from '@/components/shared/Content';
import { useElementLerp } from '@/hooks/useElementLerp';
import { useIsClient } from '@/hooks/useIsClient';
import arrow from '@/static/assets/giveback_2024/arrow.svg';
import sound_off from '@/static/assets/giveback_2024/sound_off.svg';
import sound_on from '@/static/assets/giveback_2024/sound_on.svg';
import { Flex, isIOS as isIosUA, usePageScroll, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { lerp } from '@lemonade-hq/ts-helpers';
import Image from 'next/image';
import type React from 'react';
import { type FC, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const VideoContainer = styled.div`
  width: 100%;
  position: relative;
`;

const Video = styled.video<{ ready: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ ready }) => (ready ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: calc(100% * 16 / 9);
  overflow: hidden;

  ${MEDIA_QUERIES.tabletAndDesktop`
    padding-bottom: calc(100% * 9 / 16);
  `};
`;

const Controls = styled(Flex)`
  position: absolute;
  bottom: 15px;
  right: 15px;
  justify-content: flex-end;

  ${MEDIA_QUERIES.tabletAndDesktop`
    bottom: 25px;
    right: 25px;
  `};
`;

const Sound = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
`;

const moveUpDown = keyframes`
    0%, 100% {
        transform: translate(-50%, 0);
    }
    50% {
        transform: translate(-50%, -20px);
    }
`;

const Arrow = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  justify-content: flex-end;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.5s;
  animation: ${moveUpDown} 1.5s infinite;
`;

const VIDEOS = {
  mobile: 'https://assets.lemonade.com/giveback/9x16_Giveback_LP_2025_Web_Final.webm',
  desktop: 'https://assets.lemonade.com/giveback/16x9_Giveback_LP_2025_Web_Final.webm',
};

const GivebackVideo: FC<{ t: number; video: string }> = ({ t, video }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [showArrow, setShowArrow] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('wasPlayedWithSound', 'false');
    // show down arrow when video is about to end
    const handleTimeUpdate = () => {
      if (videoRef.current) {
        const video = videoRef.current;
        const timeRemaining = video.duration - video.currentTime;
        if (timeRemaining <= 10) {
          setShowArrow(true);
        } else {
          setShowArrow(false);
        }
      }
    };

    // lower volume when scrolling
    const handleScroll = () => {
      const isIOS = isIosUA();
      const maxScroll = 1000;

      // on mobile ios we mute (cannot control volume programmatically)
      if (videoRef.current && isIOS) {
        const wasMuted = sessionStorage.getItem('wasPlayedWithSound') === 'true';
        if (!wasMuted) {
          if (window.scrollY > maxScroll) {
            videoRef.current.muted = true;
            setMuted(true);
          } else {
            setMuted(false);
          }
        }
        return;
      }

      if (videoRef.current) {
        const video = videoRef.current;
        const scrollPosition = window.scrollY;
        const volume = 1 - scrollPosition / maxScroll;
        video.volume = Math.max(0, Math.min(1, volume)); // volume is between 0 and 1
      }
    };

    const handleVisibilityChange = () => {
      if (!videoRef.current) return;

      if (document.hidden) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    const video = videoRef.current;
    if (video) {
      video.addEventListener('timeupdate', handleTimeUpdate);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (video) {
        video.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isIOS won't change
  }, []);

  const handleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setMuted(videoRef.current.muted);
      sessionStorage.setItem('wasPlayedWithSound', videoRef.current.muted.toString());
    }
  };

  const videoProps = {
    autoPlay: true,
    loop: true,
    muted: muted,
    playsInline: true,
    ref: videoRef,
    style: { borderRadius: `${lerp(0, 24, t)}px` },
    onClick: handleMute,
  };

  const [ready, setReady] = useState(false);

  return (
    <>
      <Video
        {...videoProps}
        onLoadedData={() => {
          setReady(true);
        }}
        ready={ready}
      >
        <source src={video} type="video/webm" />
      </Video>

      <Controls>
        <Sound data-state="mute" onClick={handleMute}>
          <Image alt={muted ? 'unmute' : 'mute'} src={muted ? sound_off : sound_on} />
        </Sound>
      </Controls>
      <Arrow show={showArrow}>
        <Image src={arrow} alt="arrow" />
      </Arrow>
    </>
  );
};

export const Hero: React.FC = () => {
  const { absoluteScroll } = usePageScroll();
  const { isMobileViewport } = useViewportSize();
  const [ref, t] = useElementLerp(absoluteScroll, {
    range: [0.0, 0.5],
    clamp: true,
  });

  const isClient = useIsClient();

  return (
    <Section ref={ref} darkBackground>
      <VideoContainer
        style={{
          clipPath: `inset(${lerp(0, 5, t)}% round ${lerp(0, 24, t)}px)`,
        }}
      >
        <div
          style={{
            transform: `matrix(${lerp(1, 0.9, t)}, 0, 0, ${lerp(1, 0.9, t)}, 0, 0)`,
          }}
        >
          <VideoWrapper>
            {isClient && <GivebackVideo t={t} video={isMobileViewport ? VIDEOS.mobile : VIDEOS.desktop} />}
          </VideoWrapper>
        </div>
      </VideoContainer>
    </Section>
  );
};
